import type { BuildingGraphFaceNode } from "building-types/building/buildingGraphTypes";
import type { BuildingGraphFilterBuilding } from "building-types/buildingGraphFilter/buildingGraphFilterBuilding";
import {
  BUILDING_GRAPH_FACE_OUTWARD_SIDE_DIRECTIONS,
  type PropertyPath,
} from "building-types/buildingGraphFilter/propertyCatalog";
import { RoomClass, RoomEnclosure, RoomType } from "building-types/rooms/roomTypesEnum";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { enumValues } from "common/utils/enum";
import { faceNodeGetVolumeNodeFaceIndex, faceNodeGetVolumeNodeId } from "../../buildingGraph/faceNodeParent";
import {
  faceNodeGetFreeformWallEdgeHandle,
  faceNodeGetFreeformWallId,
  faceNodeGetLevelId,
  faceNodeGetRoomClass,
  faceNodeGetRoomEnclosure,
  faceNodeGetRoomEdgeHandle,
  faceNodeGetRoomId,
  faceNodeGetRoomType,
  faceNodeGetFloorId,
  faceNodeGetRoofId,
  faceNodeGetSourceRoomId,
  faceNodeGetTopologyFaceIds,
  faceNodeGetWallPlaneKinds,
  faceNodeGetPlaneHash,
} from "../entityFilterSelectors";
import type { EntityFilterSource, PropertyDescriptorForPath, PropertyValue } from "../entityFilterSourceTypes";

const FACE_NODE_DESCRIPTORS: { [P in PropertyPath<"FACE_NODE">]: PropertyDescriptorForPath<"FACE_NODE", P> } = {
  faceNodeId: { kind: "SCALAR", type: "id" },
  volumeNodeId: { kind: "SCALAR", type: "id" },
  volumeNodeFaceIndex: { kind: "SCALAR", type: "number" },
  normalToZAxisAngle: { kind: "SCALAR", type: "number" },
  outwardSideDirection: { kind: "SCALAR", type: "enum", values: BUILDING_GRAPH_FACE_OUTWARD_SIDE_DIRECTIONS },
  planeHash: { kind: "SCALAR", type: "string" },
  isPerimeter: { kind: "SCALAR", type: "boolean" },
  isFreeform: { kind: "SCALAR", type: "boolean" },
  kinds: {
    kind: "SET",
    type: "enum",
    values: ["EXTERIOR", "ROOM_FACE", "FLOOR_FACE", "ROOF_FACE", "FREEFORM_WALL_FACE"],
  },
  roomHandle: { kind: "SCALAR", type: "roomHandle" },
  roomEdgeHandle: { kind: "SCALAR", type: "roomEdgeHandle" },
  sourceRoomHandle: { kind: "SCALAR", type: "roomHandle" },
  levelHandle: { kind: "SCALAR", type: "levelHandle" },
  roomClass: { kind: "SCALAR", type: "enum", values: enumValues(RoomClass) },
  roomEnclosure: { kind: "SCALAR", type: "enum", values: enumValues(RoomEnclosure) },
  roomType: { kind: "SCALAR", type: "enum", values: enumValues(RoomType) },
  wallPlaneHash: { kind: "SCALAR", type: "string" },
  topologyFaceIds: { kind: "SET", type: "id" },
  floorHandle: { kind: "SCALAR", type: "floorHandle" },
  roofHandle: { kind: "SCALAR", type: "roofHandle" },
  freeformWallHandle: { kind: "SCALAR", type: "freeformWallHandle" },
  freeformWallEdgeHandle: { kind: "SCALAR", type: "freeformWallEdgeHandle" },
};

function readFaceNodeProperty(
  faceNode: BuildingGraphFaceNode,
  path: PropertyPath<"FACE_NODE">,
  building: BuildingGraphFilterBuilding,
): PropertyValue {
  switch (path) {
    case "faceNodeId":
      return { kind: "SCALAR", value: faceNode.faceNodeId };
    case "volumeNodeId":
      return { kind: "SCALAR", value: faceNodeGetVolumeNodeId(faceNode) };
    case "volumeNodeFaceIndex":
      return { kind: "SCALAR", value: faceNodeGetVolumeNodeFaceIndex(faceNode) };
    case "normalToZAxisAngle":
      return { kind: "SCALAR", value: faceNode.normalToZAxisAngle };
    case "outwardSideDirection":
      return { kind: "SCALAR", value: faceNode.outwardSideDirection };
    case "planeHash":
      return { kind: "SCALAR", value: faceNodeGetPlaneHash(faceNode, building) };
    case "isPerimeter": {
      const incident = building.buildingGraph.faceNodesToEdgeIds?.[faceNode.faceNodeId];
      const hasOpposingOverlapIncident =
        incident?.some((edgeIndex) => {
          const edge = building.buildingGraph.faceEdges[edgeIndex];
          return edge?.faceEdgeType === "OVERLAP" && edge.faceEdgeDot < 0;
        }) ?? false;
      return { kind: "SCALAR", value: !hasOpposingOverlapIncident };
    }
    case "isFreeform":
      return { kind: "SCALAR", value: faceNodeGetFreeformWallId(faceNode) !== undefined };
    case "kinds":
      return { kind: "SET", values: faceNodeGetWallPlaneKinds(faceNode, building) };
    case "roomHandle": {
      const roomId = faceNodeGetRoomId(faceNode, building);
      return { kind: "SCALAR", value: roomId ? { type: "ROOM", id: roomId } : undefined };
    }
    case "roomEdgeHandle":
      return { kind: "SCALAR", value: faceNodeGetRoomEdgeHandle(faceNode, building) };
    case "sourceRoomHandle": {
      const sourceRoomId = faceNodeGetSourceRoomId(faceNode, building);
      return { kind: "SCALAR", value: sourceRoomId ? { type: "ROOM", id: sourceRoomId } : undefined };
    }
    case "levelHandle": {
      const levelId = faceNodeGetLevelId(faceNode, building);
      return { kind: "SCALAR", value: levelId ? { type: "LEVEL", id: levelId } : undefined };
    }
    case "roomClass":
      return { kind: "SCALAR", value: faceNodeGetRoomClass(faceNode, building) };
    case "roomEnclosure":
      return { kind: "SCALAR", value: faceNodeGetRoomEnclosure(faceNode, building) };
    case "roomType":
      return { kind: "SCALAR", value: faceNodeGetRoomType(faceNode, building) };
    case "wallPlaneHash":
      return { kind: "SCALAR", value: faceNodeGetPlaneHash(faceNode, building) };
    case "topologyFaceIds":
      return { kind: "SET", values: faceNodeGetTopologyFaceIds(faceNode.faceNodeId, building) };
    case "floorHandle": {
      const floorId = faceNodeGetFloorId(faceNode, building);
      return { kind: "SCALAR", value: floorId ? { type: "FLOOR", id: floorId } : undefined };
    }
    case "roofHandle": {
      const roofId = faceNodeGetRoofId(faceNode, building);
      return { kind: "SCALAR", value: roofId ? { type: "ROOF", id: roofId } : undefined };
    }
    case "freeformWallHandle": {
      const freeformWallId = faceNodeGetFreeformWallId(faceNode);
      return {
        kind: "SCALAR",
        value: freeformWallId ? { type: "FREEFORM_WALL", id: freeformWallId } : undefined,
      };
    }
    case "freeformWallEdgeHandle":
      return { kind: "SCALAR", value: faceNodeGetFreeformWallEdgeHandle(faceNode) };
    default:
      return assertUnreachable(path);
  }
}

export const faceNodeSource: EntityFilterSource<"FACE_NODE"> = {
  sourceId: "FACE_NODE",
  *iterateRows(building) {
    for (const faceNodeId in building.buildingGraph.faceNodes) {
      const faceNode = building.buildingGraph.faceNodes[faceNodeId];
      if (faceNode) {
        yield faceNode;
      }
    }
  },
  identity(faceNode) {
    return faceNode.faceNodeId;
  },
  descriptor(path) {
    return FACE_NODE_DESCRIPTORS[path];
  },
  readProperty(faceNode, path, building) {
    return readFaceNodeProperty(faceNode, path, building);
  },
};
